import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext } from 'react';
import { T, t, FormattedDateTime } from '@sm/intl';
import { createUseStyles } from 'react-jss';
import { Box } from '@wds/box';
import { Chip } from '@wds/chip';
import { IconPlus } from '@wds/icons';
import { Typography } from '@wds/typography';
import { sanitizeString } from '@sm/utils';
import ThemeMenu from '../ThemeMenu';
import ThemeBadge from './ThemeBadge';
import Badges from './Badges';
import BulletItem from './BulletItem';
import { OECTagType } from '../../../types';
import { buildThemesForResponse } from '../../../lib';
import OpenEndedCodingContext from '../../../OpenEndedCodingContext';
import { useGeneral, useThemes, useThemesReuseActions } from '../../../Context';
export const COPY = {
  responseText: {
    id: 'OpenEndedCoding.ResponseCard.responseText',
    defaultMessage: 'Response ID {respondentId}',
    description: '[Type: Header][Vis: med] - response text ID cold start'
  },
  suggested: {
    id: 'OpenEndedCoding.ResponseCard.suggested',
    defaultMessage: 'Suggested:',
    description: '[Type: Header][Vis: med] - suggested themes cold start'
  },
  applied: {
    id: 'OpenEndedCoding.ResponseCard.applied',
    defaultMessage: 'Applied:',
    description: '[Type: Header][Vis: med] - applied themes cold start'
  },
  iconPlus: {
    id: 'OpenEndedCoding.ResponseCard.iconPlus',
    defaultMessage: 'Plus icon',
    description: '[Type: Label][Vis: low] - plus icon'
  }
};
const useStyles = createUseStyles(theme => {
  const border = `1px solid ${theme.border.main}`;
  return {
    badges: {
      display: 'flex',
      flexWrap: 'wrap',
      marginBottom: `${theme.spacing[3]}px`
    },
    separator: {
      borderRight: border,
      height: `${theme.spacing[6]}px`,
      marginLeft: `${theme.spacing[3]}px`,
      marginRight: `${theme.spacing[3]}px`,
      marginTop: 'auto',
      marginBottom: 'auto'
    }
  };
});
const ResponseCard = ({
  response,
  suggested,
  setShowCreateSuggestedThemeModal,
  setSuggestedTheme,
  setSuggestedThemeResponseTextId
}) => {
  const classes = useStyles();
  const {
    handleCreateThemeForResponse,
    handleAddThemesToResponse,
    handleRemoveThemesFromResponse
  } = useContext(OpenEndedCodingContext);
  const {
    isSolutions
  } = useGeneral();
  const {
    themes
  } = useThemes();
  const {
    setSuggestedThemesForResponse
  } = useThemesReuseActions();
  // themes for responses
  const themesForResponse = buildThemesForResponse({
    response,
    themes
  });
  const themesForResponseNames = themesForResponse.map(theme => theme.theme);
  const hasThemes = themesForResponse.length > 0;
  // suggested themes
  const validSuggestedThemes = suggested.filter(s => !themesForResponseNames.includes(s));
  const hasSuggested = validSuggestedThemes.length > 0;
  // themes valid for theme menu
  const themesAvailableForSelection = themes.filter(theme => themesForResponse.filter(responseTheme => {
    var _a;
    return theme.id === responseTheme.id || ((_a = theme === null || theme === void 0 ? void 0 : theme.autogeneratedTag) === null || _a === void 0 ? void 0 : _a.id) === responseTheme.id;
  }).length === 0);
  const theDate = new Date(response.date);
  const handleAddSuggestedTheme = suggestedTheme => {
    var _a;
    const themeId = (_a = themes.find(theme => theme.theme === suggestedTheme)) === null || _a === void 0 ? void 0 : _a.id;
    // theme already exists
    if (themeId) {
      handleAddThemesToResponse([themeId], response.responseTextId);
      // remove the suggested theme
      setSuggestedThemesForResponse(suggested.filter(s => s !== suggestedTheme), response.responseTextId);
      return;
    }
    // need to create the theme, show confirmation modal
    setSuggestedTheme(suggestedTheme);
    setSuggestedThemeResponseTextId(response.responseTextId);
    setShowCreateSuggestedThemeModal(true);
  };
  return _jsxs(Box, {
    py: 4,
    children: [_jsx(Box, {
      mb: 3,
      children: _jsx(Typography, {
        component: "p",
        variant: "body",
        children: response.content
      })
    }), _jsxs(Box, {
      className: classes.badges,
      children: [_jsxs(Box, {
        display: "flex",
        children: [_jsx(ThemeMenu, {
          responseTextId: response.responseTextId,
          allThemes: themes,
          themesAvailableForSelection: themesAvailableForSelection,
          addThemesToResponse: themeIds => handleAddThemesToResponse(themeIds, response.responseTextId),
          createThemeForResponse: name => handleCreateThemeForResponse(name, response.responseTextId)
        }), _jsx("div", {
          role: "presentation",
          className: classes.separator
        })]
      }), hasSuggested && _jsx(Badges, {
        includeSeparator: hasThemes,
        text: t(COPY.suggested),
        children: validSuggestedThemes.map(s => _jsx(Box, {
          mr: 2,
          mt: 2,
          mb: 2,
          children: _jsx(Chip, {
            icon: _jsx(IconPlus, {
              title: t(COPY.iconPlus),
              color: "link"
            }),
            variant: "toggle",
            onClick: () => handleAddSuggestedTheme(s),
            children: s
          })
        }, `${response.responseTextId}-${s}`))
      }), hasThemes && _jsx(Badges, {
        text: t(COPY.applied),
        children: themesForResponse.map(({
          id,
          theme,
          tagType
        }) => _jsx(Box, {
          mr: 2,
          mt: 2,
          mb: 2,
          children: _jsx(ThemeBadge, {
            isGenerated: tagType === OECTagType.OECMODEL,
            theme: theme,
            onClose: () => handleRemoveThemesFromResponse(id, response.responseTextId)
          })
        }, id))
      })]
    }), _jsxs(Box, {
      children: [isSolutions && _jsx(BulletItem, {
        showBullet: true,
        children: _jsx("span", {
          dangerouslySetInnerHTML: {
            __html: sanitizeString(response.stimulus)
          }
        })
      }), _jsx(BulletItem, {
        showBullet: true,
        children: _jsx(T, {
          desc: COPY.responseText,
          values: {
            respondentId: response.respondentId
          }
        })
      }), _jsx(BulletItem, {
        children: _jsx("time", {
          dateTime: theDate.toISOString(),
          children: _jsx(FormattedDateTime, {
            value: theDate,
            year: "numeric",
            month: "long",
            day: "2-digit",
            hour: "numeric",
            minute: "numeric",
            timeZone: "utc"
          })
        })
      })]
    })]
  });
};
export default ResponseCard;